import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { runNodeBadgeClasses, runNodeBadgeLabel, runNodeDotClasses, runNodeFrameClasses, runNodeGlowClasses } from "@/components/workflows/node-theme";

const SUBTYPE_LABELS: Record<string, string> = {
  manual: "Manual trigger",
  schedule: "Schedule",
  webhook: "Webhook",
  channel_message: "New message on channel",
};

/**
 * Trigger node — the entry point of a workflow. One source handle (bottom),
 * no target. Green accent matching the reference screenshot.
 */
export function TriggerNode({ data, selected }: NodeProps) {
  const d = data as { label?: string; subtype?: string; description?: string; runStatus?: string; runSelected?: boolean };
  const subtypeLabel = d.subtype ? SUBTYPE_LABELS[d.subtype] ?? d.subtype : "Trigger";
  const runFrame = runNodeFrameClasses(d.runStatus);
  const runDot = runNodeDotClasses(d.runStatus);
  const runGlow = runNodeGlowClasses(d.runStatus);
  const runBadge = runNodeBadgeLabel(d.runStatus);
  const runBadgeClasses = runNodeBadgeClasses(d.runStatus);
  return (
    <div
      className={cn(
        "relative w-[240px] rounded-xl border bg-surface shadow-sm transition-all duration-300",
        runFrame ?? (selected ? "border-green-500 shadow-green-500/20" : "border-border"),
        d.runSelected && "outline outline-2 outline-offset-4 outline-accent/70",
      )}
    >
      {runGlow && (
        <span className={cn("pointer-events-none absolute -inset-3 z-0 rounded-[1.35rem]", runGlow)} />
      )}
      {runDot && (
        <span className={cn("absolute right-2 top-2 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-surface", runDot)} />
      )}
      {runBadge && runBadgeClasses && (
        <span className={cn("absolute -right-2 -top-3 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", runBadgeClasses)}>
          {runBadge}
        </span>
      )}
      <div className="flex items-center gap-1.5 bg-green-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-green-600">
        <Zap size={11} />
        Trigger
      </div>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-500/10 text-green-500">
          <Zap size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-text-primary">
            {d.label || subtypeLabel}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">
            {d.description || subtypeLabel}
          </p>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3.5 !w-3.5 !border-2 !border-surface !bg-green-500"
      />
    </div>
  );
}
